import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Camera, Check, Copy, Loader2, Mail, Phone, Percent } from 'lucide-react';

import Avatar from '@/components/Avatar';
import {
  AccrualsList,
  MemberStatsGrid,
  MonthsTable,
  PayoutsList,
  type AccrualItem,
  type MemberStatsData,
  type MonthItem,
  type PayoutItem,
} from '@/components/MemberStats';
import { apiDelete, apiErrorMessage, apiGet, apiUpload } from '@/lib/api';
import { formatBps, formatDate, formatDateTime } from '@/lib/format';
import { AVATAR_PHOTO, prepareImage } from '@/lib/image-compress';
import { telHref } from '@/lib/phone';
import { ROLE_FULL } from '@/lib/sales';
import { useAuthStore, type ManagerRole } from '@/store/auth.store';

interface Me {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  code: string;
  role: ManagerRole;
  commissionBps: number;
  avatarUrl: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  /** null — завёл администратор Resstro. */
  createdBy: { id: string; name: string } | null;
  referralLink: string;
  stats: MemberStatsData;
}

interface Summary {
  byMonth: MonthItem[];
  recentPayments: AccrualItem[];
}

export default function MyCabinet() {
  const [me, setMe] = useState<Me | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [payouts, setPayouts] = useState<PayoutItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      apiGet<Me>('/me', { silent: true }),
      apiGet<Summary>('/summary', { silent: true }),
      apiGet<PayoutItem[]>('/payouts', { silent: true }),
    ])
      .then(([m, s, p]) => {
        if (cancelled) return;
        setMe(m);
        setSummary(s);
        setPayouts(p);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Не удалось загрузить личный кабинет'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <div className="card max-w-4xl p-4 text-sm text-ink/70">{error}</div>;
  if (!me || !summary) return <div className="text-ink/50 text-sm">Загрузка…</div>;

  return (
    <div className="max-w-4xl space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold">Личный кабинет</h1>
        <p className="mt-1 text-sm text-ink/55">Ваша ставка, заведения, начисления и выплаты</p>
      </div>

      <ProfileCard me={me} onAvatar={(avatarUrl) => setMe({ ...me, avatarUrl })} />
      <MemberStatsGrid stats={me.stats} />
      <AccrualsList
        items={summary.recentPayments}
        emptyText="Пока никто из ваших заведений не оплачивал подписку."
        backState={{ backTo: '/me', backSteps: 1, backLabel: 'Личный кабинет' }}
      />
      <MonthsTable rows={summary.byMonth} />
      <PayoutsList items={payouts} />
    </div>
  );
}

/** Фото профиля: сжимаем в браузере до 1024px, сервер режет квадрат 512px. */
function AvatarEditor({
  name,
  url,
  onChange,
  children,
}: {
  name: string;
  url: string | null;
  onChange: (url: string | null) => void;
  children: React.ReactNode;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const updateManager = useAuthStore((s) => s.updateManager);

  const apply = (next: string | null) => {
    onChange(next);
    updateManager({ avatarUrl: next });
  };

  const upload = async (file: File) => {
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      toast.error('Выберите фотографию');
      return;
    }
    setBusy(true);
    try {
      const img = await prepareImage(file, AVATAR_PHOTO);
      const form = new FormData();
      form.append('file', img.blob, img.name);
      const res = await apiUpload<{ avatarUrl: string }>('/me/avatar', form, { silent: true });
      apply(res.avatarUrl);
      toast.success('Фото обновлено');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось загрузить фото'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await apiDelete('/me/avatar', { silent: true });
      apply(null);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось убрать фото'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-w-0 items-center gap-3.5">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="relative shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange/40"
        aria-label={url ? 'Сменить фото' : 'Добавить фото'}
      >
        <Avatar name={name} url={url} size={64} />
        <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-orange text-white ring-2 ring-white">
          {busy ? <Loader2 size={12} className="animate-spin" aria-hidden /> : <Camera size={12} aria-hidden />}
        </span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void upload(f);
        }}
      />
      <div className="min-w-0">
        {children}
        {/* Подписи — для тех, кто не догадается нажать на само фото. */}
        <div className="mt-1.5 flex gap-3 text-xs">
          <button
            type="button"
            className="font-medium text-orange-700 hover:underline disabled:opacity-50"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            {busy ? 'Загружаем…' : url ? 'Сменить фото' : 'Добавить фото'}
          </button>
          {url && !busy ? (
            <button type="button" className="text-ink/50 hover:text-rose-600" onClick={() => void remove()}>
              Убрать
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ me, onAvatar }: { me: Me; onAvatar: (url: string | null) => void }) {
  const [copied, setCopied] = useState(false);
  const phoneHref = telHref(me.phone);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(me.referralLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Не удалось скопировать — выделите ссылку вручную');
    }
  };

  return (
    <section className="card overflow-hidden">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
        <AvatarEditor name={me.name} url={me.avatarUrl} onChange={onAvatar}>
          <div className="truncate text-lg font-semibold leading-tight">{me.name}</div>
          <div className="mt-1 inline-flex rounded-md bg-orange/10 px-2 py-0.5 text-[11px] font-medium text-orange-700">
            {ROLE_FULL[me.role]}
          </div>
        </AvatarEditor>
        <div className="rounded-xl border border-line bg-paper px-4 py-3 sm:min-w-[220px] sm:text-right">
          <div className="flex items-center gap-1.5 text-xs text-ink/55 sm:justify-end">
            <Percent size={13} /> Личная ставка
          </div>
          <div className="mt-0.5 text-2xl font-semibold tabular-nums">{formatBps(me.commissionBps)}</div>
          <div className="mt-0.5 text-[11px] text-ink/50">с оплат подписки ваших заведений · задаёт администратор Resstro</div>
        </div>
      </div>

      <dl className="grid border-t border-line/60 text-sm sm:grid-cols-2">
        <Row label="Email">
          <a href={`mailto:${me.email}`} className="inline-flex items-center gap-1.5 hover:text-orange">
            <Mail size={13} className="text-ink/35" /> {me.email}
          </a>
        </Row>
        <Row label="Телефон">
          {me.phone && phoneHref ? (
            <a href={phoneHref} className="inline-flex items-center gap-1.5 hover:text-orange">
              <Phone size={13} className="text-ink/35" /> {me.phone}
            </a>
          ) : (
            <span className="text-ink/35">не указан</span>
          )}
        </Row>
        <Row label="Код для ссылки">
          <span className="font-mono">{me.code}</span>
        </Row>
        <Row label="В команде с">{formatDate(me.createdAt)}</Row>
        <Row label="Добавил">{me.createdBy ? me.createdBy.name : 'администратор Resstro'}</Row>
        <Row label="Последний вход">{me.lastLoginAt ? formatDateTime(me.lastLoginAt) : '—'}</Row>
      </dl>

      <div className="p-4 sm:p-5">
        <div className="mb-2 text-sm font-medium">Реферальная ссылка</div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input className="input text-xs" readOnly value={me.referralLink} onFocus={(e) => e.target.select()} />
          <button type="button" className="btn-primary w-full shrink-0 sm:w-auto" onClick={() => void copyLink()}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Скопировано' : 'Копировать'}
          </button>
        </div>
      </div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-line/60 px-4 py-3 sm:px-5 sm:odd:border-r">
      <dt className="text-ink/55">{label}</dt>
      <dd className="min-w-0 truncate text-right font-medium">{children}</dd>
    </div>
  );
}
